<template>
    <div class="d-flex justify-content-center py-3">
        <div class="card shadow-sm w-100 official-message-card">
            <div class="card-body">
                <h4 class="mb-3 text-center">Create Official Message</h4>

                <form @submit.prevent="submitForm" class="d-flex flex-column gap-3">
                    <div>
                        <label for="conversationLocale" class="form-label">Conversation locale</label>
                        <select id="conversationLocale" v-model="conversationLocale" class="form-control" required>
                            <option disabled value="">Please select one</option>
                            <option>EN</option>
                            <option>LT</option>
                            <option>LV</option>
                            <option>ET</option>
                            <option>IT</option>
                            <option>ES</option>
                            <option>RO</option>
                            <option>UA</option>
                        </select>
                    </div>

                    <div>
                        <label for="subject" class="form-label">Subject</label>
                        <input
                                v-model="subject"
                                id="subject"
                                type="text"
                            class="form-control subject-input"
                                placeholder="Enter subject"
                                required
                        >
                    </div>

                    <div class="form-group">
                        <label for="message" class="form-label">Message</label>
                        <ckeditor
                            v-model="message"
                            :editor="editor"
                            :config="editorConfig"
                        ></ckeditor>
                    </div>

                    <div>
                        <label for="files" class="form-label">Attachments</label>
                        <input
                                id="files"
                                ref="filesInput"
                                type="file"
                                class="form-control"
                                multiple
                                @change="handleFilesChange"
                        >
                        <div class="mt-2">
                            <button
                                    type="button"
                                    class="btn btn-outline-secondary btn-sm"
                                    @click="clearFiles"
                                    :disabled="!selectedFiles.length"
                            >
                                Clear files
                            </button>
                        </div>
                        <div v-if="selectedFiles.length" class="mt-2 d-flex flex-column gap-1">
                            <small class="text-muted">Selected files:</small>
                            <small v-for="(file, index) in selectedFiles" :key="`${file.name}-${index}`" class="text-break">
                                {{ file.name }}
                            </small>
                        </div>
                    </div>

                    <div v-if="errorMessage" class="alert alert-danger mb-0">
                        {{ errorMessage }}
                    </div>

                    <div v-if="successMessage" class="alert alert-success mb-0">
                        {{ successMessage }}
                    </div>

                    <div class="d-flex justify-content-end">
                        <button type="submit" class="btn btn-primary" :disabled="loading">
                            {{ loading ? 'Sending...' : 'Send Official Message' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import { 
    ClassicEditor, 
    Essentials, 
    Paragraph, 
    Heading, 
    Bold, 
    Italic, 
    Link, 
    List, 
    Indent, 
    IndentBlock, 
    Image, 
    ImageUpload, 
    SimpleUploadAdapter, 
    MediaEmbed,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageBlock
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';


export default {
    components: {
        Ckeditor,
    },
    data() {
        return {
            conversationLocale: '',
            subject: '',
            message: '',
            selectedFiles: [],
            loading: false,
            errorMessage: '',
            successMessage: '',
            editor: ClassicEditor,
        };
    },
    computed: {
        editorConfig() {
            const baseUrl = (axios.defaults.baseURL || '').replace(/\/$/, '');
            const token = this.$store?.state?.auth?.token || this.$store?.state?.token || localStorage.getItem('token') || '';
            const headers = {
                Accept: 'application/json',
            };

            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            return {
                licenseKey: 'GPL',
                plugins: [
                    Essentials,
                    Paragraph,
                    Heading,
                    Bold,
                    Italic,
                    Link,
                    List,
                    Indent,
                    IndentBlock,
                    Image,
                    ImageUpload,
                    SimpleUploadAdapter,
                    MediaEmbed,
                    ImageResize,
                    ImageStyle,
                    ImageToolbar,
                    ImageBlock
                ],
                toolbar: [
                    'undo',
                    'redo',
                    '|',
                    'heading', 'bold', 'italic', 'link',
                    '|',
                    'bulletedList', 'numberedList', 'outdent', 'indent',
                    '|',
                    'imageUpload',
                    'mediaEmbed',
                ],
                mediaEmbed: {
                    previewsInData: true,
                },
                simpleUpload: {
                    uploadUrl: `${baseUrl}/admin/conversations/messages/official/image`,
                    headers,
                    withCredentials: true,
                },
                image: {
                    toolbar: [
                        'imageStyle:alignBlockLeft',
                        'imageStyle:block',
                        'imageStyle:alignBlockRight',
                        '|',
                        'imageResize',
                    ],
                }
            };
        },
        currentUserId() {
            return this.$store?.state?.auth?.user?.id || null;
        },
        submitUrl() {
            return this.currentUserId
                    ? `admin/conversations/messages/official`
                    : '';
        },
    },
    methods: {

        handleFilesChange(event) {
            this.selectedFiles = Array.from(event.target.files || []);
        },

        clearFiles() {
            this.selectedFiles = [];
            if (this.$refs.filesInput) {
                this.$refs.filesInput.value = '';
            }
        },

        async submitForm() {
            this.errorMessage = '';
            this.successMessage = '';

            if (!this.currentUserId) {
                this.errorMessage = 'Unable to resolve current user id.';
                return;
            }

            if (!this.conversationLocale) {
                this.errorMessage = 'Please select a conversation locale.';
                return;
            }

            const subject = this.subject.trim();
            if (!subject) {
                this.errorMessage = 'Please enter a subject.';
                return;
            }

            const message = this.message.trim();
            if (!message) {
                this.errorMessage = 'Please enter a message.';
                return;
            }

            const formData = new FormData();
            formData.append('conversation_locale', this.conversationLocale.toLocaleLowerCase());
            formData.append('subject', subject);
            formData.append('message', message);
            formData.append('user_id', this.currentUserId);
            this.selectedFiles.forEach((file) => {
                formData.append('files[]', file);
            });

            this.loading = true;

            try {
                await axios.post(this.submitUrl, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    transformRequest: [(data) => data],
                });

                this.successMessage = 'Official message prepared successfully.';
                this.subject = '';
                this.message = '';
                this.clearFiles();
            } catch (error) {
                console.error('Error sending official message:', error);
                this.errorMessage = 'Unable to send the official message.';
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.official-message-card {
    max-width: 920px;
}

.subject-input {
    min-height: 44px;
}

:deep(.ck-editor__editable_inline) {
    min-height: 220px;
}

.gif-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
}

.gif-card {
    padding: 0;
    border: 1px solid #dee2e6;
    border-radius: 0.75rem;
    overflow: hidden;
    background: #fff;
    transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.gif-card:hover {
    transform: translateY(-2px);
    border-color: #0d6efd;
    box-shadow: 0 0.5rem 1rem rgba(13, 110, 253, 0.12);
}

.gif-card--selected {
    border-color: #0d6efd;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
}

.gif-card__image,
.selected-gif__image {
    width: 100%;
    display: block;
    object-fit: cover;
}

.selected-gif__image {
    width: 120px;
    border-radius: 0.5rem;
}
</style>